import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Edit, Trash2, Eye, Search, Code, Tag, Clock,
  ChevronRight, X, Save, Loader2, FileText, CheckCircle,
  AlertTriangle, Copy, Zap
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompanyProblemsPage = () => {
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProblem, setEditingProblem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [previewProblem, setPreviewProblem] = useState(null);
  
  const token = localStorage.getItem('company_token');
  
  const [form, setForm] = useState({
    title: '',
    description: '',
    input_format: '',
    output_format: '',
    constraints: '',
    sample_test_cases: [],
    hidden_test_cases: [],
    difficulty: 'medium',
    tags: [],
    time_limit_ms: 2000,
    memory_limit_mb: 256,
    expected_output_format: '',
    evaluation_criteria: ''
  });
  
  const [newTestCase, setNewTestCase] = useState({ input: '', output: '', explanation: '', is_hidden: false });
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    fetchProblems();
  }, []);

  const fetchProblems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/company/problems`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProblems(res.data.problems || []);
    } catch (err) {
      toast.error('Failed to load problems');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      title: '',
      description: '',
      input_format: '',
      output_format: '',
      constraints: '',
      sample_test_cases: [],
      hidden_test_cases: [],
      difficulty: 'medium',
      tags: [],
      time_limit_ms: 2000,
      memory_limit_mb: 256,
      expected_output_format: '',
      evaluation_criteria: ''
    });
    setEditingProblem(null);
  };

  const openEditModal = async (problem) => {
    try {
      const res = await axios.get(`${API}/company/problems/${problem.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingProblem(res.data);
      setForm(res.data);
      setShowModal(true);
    } catch (err) {
      toast.error('Failed to load problem details');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description) {
      toast.error('Title and description are required');
      return;
    }
    
    try {
      if (editingProblem) {
        await axios.put(`${API}/company/problems/${editingProblem.id}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Problem updated');
      } else {
        await axios.post(`${API}/company/problems`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Problem created');
      }
      setShowModal(false);
      resetForm();
      fetchProblems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save problem');
    }
  };

  const handleDelete = async (problemId) => {
    if (!window.confirm('Are you sure you want to delete this problem?')) return;
    
    try {
      await axios.delete(`${API}/company/problems/${problemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Problem deleted');
      fetchProblems();
    } catch (err) {
      toast.error('Failed to delete problem');
    }
  };

  const addTestCase = () => {
    if (!newTestCase.input || !newTestCase.output) return;
    
    const field = newTestCase.is_hidden ? 'hidden_test_cases' : 'sample_test_cases';
    setForm(prev => ({
      ...prev,
      [field]: [...prev[field], { ...newTestCase }]
    }));
    setNewTestCase({ input: '', output: '', explanation: '', is_hidden: false });
  };

  const removeTestCase = (field, index) => {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const addTag = () => {
    if (newTag && !form.tags.includes(newTag)) {
      setForm(prev => ({ ...prev, tags: [...prev.tags, newTag] }));
      setNewTag('');
    }
  };

  const removeTag = (tag) => {
    setForm(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const getDifficultyColor = (diff) => {
    const colors = {
      easy: 'text-green-400 bg-green-500/20',
      medium: 'text-yellow-400 bg-yellow-500/20',
      hard: 'text-red-400 bg-red-500/20'
    };
    return colors[diff] || colors.medium;
  };

  const filteredProblems = problems.filter(p =>
    p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.tags?.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-[#030303] text-white p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Custom Problems</h1>
            <p className="text-gray-400">Create and manage your own assessment problems</p>
          </div>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-lg font-medium hover:opacity-90"
            data-testid="create-problem-btn"
          >
            <Plus size={18} />
            Create Problem
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search problems by title or tags..."
            className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50"
          />
        </div>

        {/* Problems List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-purple-400" size={32} />
          </div>
        ) : filteredProblems.length === 0 ? (
          <div className="text-center py-12 bg-white/5 rounded-xl border border-white/10">
            <Code className="mx-auto text-gray-600 mb-4" size={48} />
            <h3 className="text-lg font-semibold text-white mb-2">No problems yet</h3>
            <p className="text-gray-400 mb-4">Create your first custom problem for assessments</p>
            <button
              onClick={() => { resetForm(); setShowModal(true); }}
              className="px-4 py-2 bg-purple-500 text-white rounded-lg"
            >
              Create Problem
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredProblems.map(problem => (
              <div
                key={problem.id}
                className="bg-white/5 border border-white/10 rounded-xl p-5 hover:border-purple-500/30 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-white">{problem.title}</h3>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getDifficultyColor(problem.difficulty)}`}>
                        {problem.difficulty}
                      </span>
                      <span className="text-xs text-gray-500">v{problem.version || 1}</span>
                    </div>
                    <p className="text-gray-400 text-sm mb-3 line-clamp-2">{problem.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {problem.tags?.map((tag, idx) => (
                        <span key={idx} className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => setPreviewProblem(problem)}
                      className="p-2 bg-white/5 text-gray-400 hover:text-white rounded-lg"
                      title="Preview"
                    >
                      <Eye size={18} />
                    </button>
                    <button
                      onClick={() => openEditModal(problem)}
                      className="p-2 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded-lg"
                      title="Edit"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(problem.id)}
                      className="p-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg"
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-4xl my-8">
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-xl font-bold text-white">
                {editingProblem ? 'Edit Problem' : 'Create New Problem'}
              </h2>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm text-gray-400 mb-2">Problem Title *</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500"
                    placeholder="e.g., Two Sum Problem"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Difficulty</label>
                  <select
                    value={form.difficulty}
                    onChange={(e) => setForm(f => ({ ...f, difficulty: e.target.value }))}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500"
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Time Limit (ms)</label>
                  <input
                    type="number"
                    value={form.time_limit_ms}
                    onChange={(e) => setForm(f => ({ ...f, time_limit_ms: parseInt(e.target.value) }))}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">Problem Description *</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={4}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500"
                  placeholder="Describe the problem in detail..."
                  required
                />
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Input Format</label>
                  <textarea
                    value={form.input_format}
                    onChange={(e) => setForm(f => ({ ...f, input_format: e.target.value }))}
                    rows={3}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500"
                    placeholder="Describe the input format..."
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Output Format</label>
                  <textarea
                    value={form.output_format}
                    onChange={(e) => setForm(f => ({ ...f, output_format: e.target.value }))}
                    rows={3}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500"
                    placeholder="Describe the expected output format..."
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">Constraints</label>
                <textarea
                  value={form.constraints}
                  onChange={(e) => setForm(f => ({ ...f, constraints: e.target.value }))}
                  rows={2}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500"
                  placeholder="e.g., 1 <= n <= 10^5"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">Evaluation Criteria</label>
                <textarea
                  value={form.evaluation_criteria}
                  onChange={(e) => setForm(f => ({ ...f, evaluation_criteria: e.target.value }))}
                  rows={2}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500"
                  placeholder="How will solutions be evaluated?"
                />
              </div>
              
              {/* Tags */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Tags</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    placeholder="Add tag (e.g., arrays, dp, strings)"
                  />
                  <button type="button" onClick={addTag} className="px-4 py-2 bg-purple-500/20 text-purple-400 rounded-lg">
                    <Plus size={18} />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {form.tags.map((tag, idx) => (
                    <span key={idx} className="flex items-center gap-1 px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm">
                      {tag}
                      <button type="button" onClick={() => removeTag(tag)}>
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              
              {/* Test Cases */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Sample Test Cases</label>
                <div className="space-y-2 mb-3">
                  {form.sample_test_cases.map((tc, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-3 bg-white/5 rounded-lg">
                      <div className="flex-1 grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-500">Input:</span>
                          <pre className="text-white">{tc.input}</pre>
                        </div>
                        <div>
                          <span className="text-gray-500">Output:</span>
                          <pre className="text-green-400">{tc.output}</pre>
                        </div>
                      </div>
                      <button type="button" onClick={() => removeTestCase('sample_test_cases', idx)} className="text-red-400">
                        <X size={18} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={newTestCase.input}
                    onChange={(e) => setNewTestCase(tc => ({ ...tc, input: e.target.value }))}
                    className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
                    placeholder="Input"
                  />
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newTestCase.output}
                      onChange={(e) => setNewTestCase(tc => ({ ...tc, output: e.target.value }))}
                      className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
                      placeholder="Expected Output"
                    />
                    <button type="button" onClick={addTestCase} className="px-3 py-2 bg-green-500/20 text-green-400 rounded-lg">
                      <Plus size={18} />
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="px-6 py-3 bg-white/5 text-white rounded-xl hover:bg-white/10"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl font-medium hover:opacity-90"
                >
                  {editingProblem ? 'Update Problem' : 'Create Problem'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewProblem && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-3xl my-8">
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div>
                <h2 className="text-xl font-bold text-white">{previewProblem.title}</h2>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getDifficultyColor(previewProblem.difficulty)}`}>
                  {previewProblem.difficulty}
                </span>
              </div>
              <button onClick={() => setPreviewProblem(null)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              <div>
                <h3 className="text-sm font-semibold text-gray-400 mb-2">Problem Statement</h3>
                <p className="text-white whitespace-pre-wrap">{previewProblem.description}</p>
              </div>
              
              {previewProblem.input_format && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-400 mb-2">Input Format</h3>
                  <p className="text-gray-300 whitespace-pre-wrap">{previewProblem.input_format}</p>
                </div>
              )}
              
              {previewProblem.output_format && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-400 mb-2">Output Format</h3>
                  <p className="text-gray-300 whitespace-pre-wrap">{previewProblem.output_format}</p>
                </div>
              )}
              
              {previewProblem.constraints && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-400 mb-2">Constraints</h3>
                  <p className="text-gray-300 whitespace-pre-wrap">{previewProblem.constraints}</p>
                </div>
              )}
              
              {previewProblem.sample_test_cases?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-400 mb-2">Sample Test Cases</h3>
                  <div className="space-y-3">
                    {previewProblem.sample_test_cases.map((tc, idx) => (
                      <div key={idx} className="bg-white/5 rounded-lg p-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <span className="text-xs text-gray-500">Input</span>
                            <pre className="text-white text-sm mt-1 bg-black/30 p-2 rounded">{tc.input}</pre>
                          </div>
                          <div>
                            <span className="text-xs text-gray-500">Output</span>
                            <pre className="text-green-400 text-sm mt-1 bg-black/30 p-2 rounded">{tc.output}</pre>
                          </div>
                        </div>
                        {tc.explanation && (
                          <p className="text-gray-400 text-sm mt-2">{tc.explanation}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {previewProblem.evaluation_criteria && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-400 mb-2">Evaluation Criteria</h3>
                  <p className="text-gray-300">{previewProblem.evaluation_criteria}</p>
                </div>
              )}
              
              {previewProblem.tags?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-400 mb-2">Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {previewProblem.tags.map((tag, idx) => (
                      <span key={idx} className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyProblemsPage;
